import { createAppThunk } from '@mse-reg/app/store/app-thunk';
import { ILabelClient, IProfileClient, LabelDto, ProfileDto } from '@mse-reg/clients';
import { PayloadAction, createSlice } from '@reduxjs/toolkit';

const fetchLabelsAsync = createAppThunk(
  'profileSelection/SET_LABELS',
  async (_, deps: { labelClient: ILabelClient }) => {
    return await deps.labelClient.list();
  },
  (r) => ({
    labelClient: r.resolve<ILabelClient>('ILabelClient'),
  }),
  'An error occurred while trying to fetch labels.',
);

const fetchProfilesAsync = createAppThunk(
  'profileSelection/SET_PROFILES',
  async (_, deps: { profileClient: IProfileClient }) => {
    return await deps.profileClient.list();
  },
  (r) => ({
    profileClient: r.resolve<IProfileClient>('IProfileClient'),
  }),
  'An error occurred while trying to fetch profiles.',
);

// State
const initialState = {
  labels: [] as LabelDto[],
  profiles: [] as ProfileDto[],
  loading: false,
};

export type ProfileSelectionState = Readonly<typeof initialState>;

const slice = createSlice({
  name: 'profileSelection',
  initialState,
  reducers: {
    setLabels(state, action: PayloadAction<LabelDto[]>) {
      state.labels = action.payload;
    },
    setProfiles(state, action: PayloadAction<ProfileDto[]>) {
      state.profiles = action.payload;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchLabelsAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchLabelsAsync.fulfilled, (state, action) => {
        state.labels = action.payload;
      })
      .addCase(fetchLabelsAsync.rejected, (state) => {
        state.loading = false;
      })
      .addCase(fetchProfilesAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchProfilesAsync.fulfilled, (state, action) => {
        state.profiles = action.payload;
      })
      .addCase(fetchProfilesAsync.rejected, (state) => {
        state.loading = false;
      });
  },
});

export const profileSelectionReducer = slice.reducer;
export const profileSelectionActions = slice.actions;
export const profileSelectionThunks = { fetchLabelsAsync, fetchProfilesAsync };

export type FetchProfilesThunkType = typeof fetchProfilesAsync;
